<template>
  <vue-js-modal
    v-if="input"
    :key="reload"
    id="agChildInputModal"
    class="agModal"
    name="agChildInputModal"
    draggable=".card-header"
    width="75%"
    height="85%"
    :shiftX=".5"
    :shiftY=".25"
    :resizable="true"
    :clickToClose="false">
    <b-card>
      <template #header>
        <h4 v-if="disableUI || isLoading" class="agModal-title">View Input {{input.shortname}}</h4>
        <h4 v-else class="agModal-title">Edit Input {{input.shortname}}</h4>
      </template>

      <b-input-group size="md" prepend="Name">
        <b-input
          type="text"
          v-model="input.name"
          autocomplete="false"
          :disabled="disableUI || isLoading"
        ></b-input>
      </b-input-group>
      <hr/>

      <ag-fields-selector
        ref="fieldsSelector"
        :node="input"
        :disableUI="disableUI || isLoading"
        :connections="connections"
        @refreshedSchemas="refreshedSchemas"
        @connectionChanged="connectionChanged"
        @layerChanged="layerChanged">
      </ag-fields-selector>
      <div v-if="showGeometry" class="agInputGroup agInputGroupCentered">
        <input
          type="checkbox"
          v-model="input.includeGeometry"
          :disabled="disableUI || isLoading"/>
        <label>Include geometry</label>
      </div>
      <div class="agInputGroup">
        <ag-sub-input
          ref="wClause"
          :initText="input.wClause"
          :workflow="workflow"
          :size="'md'"
          :prepend="'Where'"
          :appendButton="true"
          :buttonText="'Test'"
          :disabled="disableUI || isLoading"
          @buttonClick="testWClause_click">
        </ag-sub-input>
      </div>
      <label
        id="agChildInputModal-testMessage"
        class="agModal-message"
        :class="testMessageClass"
        @click="testMessage_click">
        {{ testMessage }}
      </label>
      <div v-if="queryParams.length > 0" class="agInputGroup">
        <label>Query Parameters:</label>
        <b-table
          id="agChildInputModal-queryParamsTable"
          :key="'queryParams-' + reloadQueryParams"
          :items="queryParams"
          :fields="queryParamHeaders"
          borderless
          outlined
          small>
          <template #cell(alias)="data">
            <b-input
              class="agChildInputModal-queryParamField"
              :value="data.value"
              autocomplete="false"
              disabled>
            </b-input>
          </template>
          <template slot="=">
            <div class="agChildInputModal-queryParamEquals">=</div>
          </template>
          <template #cell(domain)="data">
            <b-form-select
              v-if="data.item.domain"
              v-model="data.item.value"
              class="agChildInputModal-queryParamValue"
              :options="data.item.domain"
              :disabled="disableUI || isUpdatingSchemas"
              @change="queryParamValue_change($event, data.index)">
            </b-form-select>
            <b-input
              v-else
              v-model="data.item.value"
              class="agChildInputModal-queryParamValue"
              type="text"
              autocomplete="false"
              :disabled="disableUI || isUpdatingSchemas">
            </b-input>
          </template>
        </b-table>
      </div>
      
      <template #footer>
        <b-spinner v-if="isLoading" class="agModal-loadingSpinner"></b-spinner>
        <div v-else-if="modalMessage">
          <label class="agModal-message" :class="modalMessageClass">{{modalMessage}}</label>
        </div>

        <b-button class="agModal-buttonLeft" @click="cancelButton_click">Cancel</b-button>
        <b-button
          class="agModal-buttonRight"
          variant="primary"
          :disabled="disableUI || isLoading"
          @click="saveButton_click">
          Save
        </b-button>
        <b-button
          class="agModal-buttonRight"
          variant="danger"
          :disabled="disableUI || isLoading"
          @click="deleteButton_click">
          Delete
        </b-button>
      </template>
    </b-card>

    <!-- delete modal -->
    <b-modal
      class="agModal"
      ref="deleteModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      @keyup.enter.native="deleteButton_click">
      <template slot="modal-header">
        <h4 class="agModal-title danger">Delete Input</h4>
      </template>
      Are you sure you want to delete input
      <i>{{ name }}</i>?
      <template slot="modal-footer" slot-scope="{ cancel }">
        <b-button class="agModal-buttonLeft" @click="cancel">Cancel</b-button>
        <b-button
          class="agModal-buttonRight"
          variant="danger"
          @click="confirmDeleteButton_click">
          Delete
        </b-button>
      </template>
    </b-modal>

  </vue-js-modal>
</template>

<script>
// components
import SubInput from "comp/ui/SubInput";
import FieldsSelector from "comp/ui/FieldsSelector";

// modules
import Vue from "vue";
import Server from "src/server";
import Util from "src/util/util";
import { EventBus } from "src/eventBus";

export default {
  name: "ag-input-modal",
  components: {
    "ag-sub-input": SubInput,
    "ag-fields-selector": FieldsSelector
  },
  props: {
    workflow: Object,
    disableUI: Boolean
  },
  data() {
    return {
      input: null,
      reload: 0,
      reloadQueryParams: 0,
      isLoading: false,
      isUpdatingSchemas: false,
      modalMessage: "",
      modalMessageClass: "",
      testMessage: "",
      testMessageClass: "",
      testRecords: [],
      queryParamHeaders: [
        { key: "alias", label: "Parameter" },
        { key: "=", label: "=" },
        { key: "domain", label: "Value" }
      ],
      queryParams: [],
      queryParamsCancel: null,
      showGeometry: false
    };
  },
  computed: {

    name() {
      if (this.input.name) {
        return this.input.name;
      }
      return this.input.shortname;
    },

    connections() {
      var inputOutputConnections = this.$store.state.connStore.connections;
      if(this.input.connection){
        let newConn = inputOutputConnections.find(c => c._id == this.input.connection._id)
        this.input.connection = newConn;
      }
      return inputOutputConnections.filter(c => c.type != "geometry").map(b => {return {value: b, text: b.name};});
    }
  },

  watch: {},

  methods: {

    // UI Events

    cancelButton_click() {
      this.hide();
    },

    async saveButton_click() {
      this.modalMessage = "";

      // build input.fields w/ checked fields
      this.input.fields = [];
      for (var i = 0; i < this.$refs.fieldsSelector.fields.length; i++) {
        var field = this.$refs.fieldsSelector.fields[i];
        if (field.enabled) {
          this.input.fields.push(field.field);
        }
      }

      // manually pull wClause from subInput component
      this.input.wClause = this.$refs["wClause"].text;

      // build input.queryParams only w/ queryParams that have a value set
      var validParams = [];
      for (var i = 0; i < this.queryParams.length; i++) {
        var param = this.queryParams[i];
        if (param.value) {
          validParams.push({ param: param.field, value: param.value });
        }
      }
      this.input.queryParams = validParams;

      let node = {
        object: this.input,
        type: "input"
      };
      this.isLoading = true;

      let command = "nodeStore/updateNode";
      if(!this.input._id)
        command = "nodeStore/insertNode";

      let result = await this.$store.dispatch(command, { node: node });
      this.isLoading = false;
      if (result.data.success) {
        this.hide();
        this.$emit("updatedNode", result.data.node);
      } 
      else {
        this.modalMessage = result.data.error;
        this.modalMessageClass = "agModal-errorMessage";
      }
    },

    deleteButton_click() {
      this.$refs.deleteModal.show();
    },

    async confirmDeleteButton_click() {
      this.$refs.deleteModal.hide();
      var node = {
        object: this.input,
        type: "input"
      };
      this.isLoading = true;
      var result = await this.$store.dispatch("nodeStore/deleteNode", {
        node: node
      });
      this.$emit("deletedNode", node);
      this.isLoading = false;
      this.hide();
    },

    hide(){
      this.$modal.hide('input');
    },

    async testWClause_click() {
      try {
        this.testRecords = [];
        this.testMessage = "";

        if (!this.input.connection) {
          this.testRecords = [];
          this.testMessage = "A connection must be selected";
          this.testMessageClass = "agModal-errorMessage";
          return;
        }

        if (this.input.layer == null) {
          this.testRecords = [];
          this.testMessage = "A layer/object must be selected";
          this.testMessageClass = "agModal-errorMessage";
          return;
        }
        var fields = [];
        for (var i = 0; i < this.$refs.fieldsSelector.fields.length; i++) {
          var field = this.$refs.fieldsSelector.fields[i];
          if (field.enabled) {
            fields.push(field.field);
          }
        }

        this.isLoading = true;
        let wClause = this.$refs["wClause"].text;
        let result = await Server.post("getRecords", {
          connection: this.input.connection._id,
          layer: this.input.layer,
          fields: fields,
          wClause: wClause
        }).promise;
        this.isLoading = false;
        if (!result.data.success)
         throw result.data.error;
        //this is for when the include geometry button is not clicked it wont display the geometry data. Vice Versa, if its checked then it will display geometry data
        if(!this.input.includeGeometry || this.input.includeGeometry === undefined){
          const obj = result.data.records.map(a => { 
            if(a.geometry)
              delete a.geometry
            return a
          })
          this.testRecords = obj
        }else{
          this.testRecords = result.data.records;
        }
        this.testMessage = `Success: ${
          this.testRecords.length
        } record(s) found`;
        this.testMessageClass = "agModal-successMessage";
      } catch (e) {
        this.testRecords = [];
        this.testMessage = e.toString();
        this.testMessageClass = "agModal-errorMessage";
        console.log("Failed 'testWClause' server call. " + e.toString());
      }
    },

    testMessage_click() {
      if (this.testMessageClass.indexOf("success") <= 0) return;

      EventBus.$emit("showAlert", {
        title: `${this.testRecords.length} Record(s)`,
        message: JSON.stringify(this.testRecords, null, 4),
        type: "default"
      });
    },

    queryParamValue_change(value, index) {
      var queryParam = this.queryParams[index];
      if (value == "-- None --") {
        queryParam.value = null;
        this.reloadQueryParams++;
      }
    },

    // emitted events

    refreshedSchemas() {
      this.setQueryParams();
    },

    connectionChanged() {
      if (!this.input.connection || this.input.connection.type == "max")
        this.showGeometry = false;
      else this.showGeometry = true;

      this.setQueryParams();
    },

    layerChanged() {
      this.setQueryParams();
    },

    // other

    async show(input) {
      this.input = input;
      await Vue.nextTick();
      await this.reset();
      this.$modal.show("agChildInputModal");
    },

    hide() {
      this.$modal.hide("agChildInputModal");
    },

    async reset() {
      this.modalMessage = "";
      this.modalMessageClass = "";
      this.errorField = "";
      this.testRecords = [];
      this.testMessage = "";
      this.testMessageClass = "";
      if (!this.input.connection || this.input.connection.type == "max")
        this.showGeometry = false;
      else 
        this.showGeometry = true;

      this.setQueryParams();
      var self = this;
      setTimeout(() => {
        self.$refs.fieldsSelector.reset();
      }, 250);
    },

    async setQueryParams() {
      this.queryParams = [];
      if (this.input.layer != null) {
        // make sure to keep != null cause layer can be 0
        var schema = this.input.connection.schemas.find(
          schema => schema.id == this.input.layer
        );
        if (!schema || !schema.queryParams) return;

        for (var i = 0; i < schema.queryParams.length; i++) {
          var param = JSON.parse(JSON.stringify(schema.queryParams[i]));
          if (param.domain) {
            var options = [{ value: "-- None --", text: "-- None --" }];
            for (var j = 0; j < param.domain.length; j++) {
              var domain = param.domain[j];
              options.push({ value: domain.value, text: domain.alias, alias: domain.alias });
            }
            param.domain = options;
          }

          // find and populate from input.queryParams or else push blank field
          if (!this.input.queryParams) this.input.queryParams = [];
          var existing = this.input.queryParams.find(
            a => a.param == param.field
          );
          if (existing) {
            this.queryParams.push({
              field: param.field,
              alias: param.alias,
              domain: param.domain,
              value: existing.value
            });
          } else {
            this.queryParams.push({
              field: param.field,
              alias: param.alias,
              domain: param.domain,
              value: null
            });
          }
        }
      }
    }
  }
};
</script>

<style scoped lang="css">

#agChildInputModal-fieldsTable
{
    display: inline-block;
    vertical-align: top;
    width: calc(100% - 155px);
    height: 175px;
    margin: 5px 0px 0px 100px;
    overflow: auto;
    border-collapse: collapse
}

#agChildInputModal-fieldsTableSpinner {
  position: relative;
  left: -500px;
  top: 75px;
}

#agChildInputModal-fieldSearch {
  width: calc(100% - 200px);
}

#agChildInputModal-hideUnselectedFields {
  position: relative;
  left: 111px;
  top: 10px;
  margin-bottom: 5px;
}

#agChildInputModal-hideUnselectedFields label {
  position: relative;
  top: -2px;
  left: 5px;
}

#agChildInputModal-testMessage {
  position: relative;
  width: 100%;
  top: -5px;
  text-align: center !important;
}

#agChildInputModal-testMessage.agModal-successMessage:hover {
  cursor: pointer;
  text-decoration: underline;
}

#agChildInputModal-testMessage.agModal-successMessage:active {
  opacity: 0.5;
}

#agChildInputModal-queryParamsTable
{
    display: inline-block;
    vertical-align: top;
    width: calc(100% - 195px);
    height: 175px;
    overflow: auto;
    text-align: center;
    border-collapse: collapse
}

.agChildInputModal-queryParamField {
  width: 375px;
}

.agChildInputModal-queryParamEquals {
  width: 100px;
  margin-top: 7.5px;

}

.agChildInputModal-queryParamValue {
  width: 375px;
}


</style>



// WEBPACK FOOTER //
// src/components/nodes/ChildInputModal.vue